import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useCallback, useMemo, useReducer} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import CSVFilePreview from 'external-files/CSVFilePreview';
import {ConnectionStatus, InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import {AutomationOutputFileConfig} from 'lab-automation/automation-files/LabAutomationConstants';
import {ClientAutomationOutputFileConfigSpec} from 'lab-automation/settings/RunSchemaSpecConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import * as DateUtil from 'util/DateUtil';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {
  ImportConnectionFilesModalState,
  StepKey,
  INITIAL_STATE,
  ConnectionFileStatus,
} from './ConnectionFilesConstants';
import ConnectionFilesTable, {getConnectionFileStatus} from './ConnectionFilesTable';
import reducer, {
  ImportConnectionFilesModalAction,
  ImportConnectionFilesModalActionType,
} from './importConnectionFilesModalReducer';

const PREVIEWABLE_FILE_TYPES = ['.csv', '.txt'];

export interface ImportConnectionFilesModalProps {
  allowMultiple: boolean;
  automationOutputFileConfig: AutomationOutputFileConfig | ClientAutomationOutputFileConfigSpec;
  connectionSchemaId?: string;
  connectionId?: string;
  onRequestClose: () => void;
  onRequestSubmit: (connectionFiles: Array<FileListingGraphQL>) => void | Promise<void>;
  attachAndProcessFiles?: (
    attachConnectionFiles: Array<FileListingGraphQL>,
    processConnectionFiles: Array<FileListingGraphQL>
  ) => void | Promise<void>;
  instrumentServiceDetails: InstrumentServiceDetails | null;
}

function endsWithAny(path: string, suffixes: Array<string>): boolean {
  return suffixes.some((suffix) => path.endsWith(suffix));
}

function getStepButtons(
  state: ImportConnectionFilesModalState,
  dispatch: Dispatch<ImportConnectionFilesModalAction>,
  onRequestSubmit: (connectionFiles: Array<FileListingGraphQL>) => void | Promise<void>,
  attachAndProcessFiles?: (
    attachConnectionFiles: Array<FileListingGraphQL>,
    processConnectionFiles: Array<FileListingGraphQL>
  ) => void | Promise<void>
): Array<ModalButton> {
  if (state.currentStepKey === StepKey.ATTACH) {
    return [
      {
        // Can skip attaching if at least one file has been selected for processing.
        text: state.isImporting
          ? 'Processing...'
          : state.selectedRowIndices.size === 0 && state.selectedProcessConnectionFiles.length > 0
            ? 'Skip'
            : 'Done',
        // Can submit if at least one file has been selected for attaching or processing.
        disabled:
          state.isImporting ||
          state.selectedRowIndices.size + state.selectedProcessConnectionFiles.length === 0,
        submit: true,
        onClick: async () => {
          dispatch({
            type: ImportConnectionFilesModalActionType.IS_IMPORTING,
            isImporting: true,
          });
          try {
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT);
            // NOTE: this check is just to make type checks happy. We will never enter the ATTACH step when
            // attachAndProcessFiles is undefinded, because that is the condition for entering the step.
            if (attachAndProcessFiles) {
              await attachAndProcessFiles(
                [...state.selectedRowIndices].map((index: number) => state.connectionFiles[index]),
                state.selectedProcessConnectionFiles
              );
            }
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT);
          } finally {
            dispatch({
              type: ImportConnectionFilesModalActionType.IS_IMPORTING,
              isImporting: false,
            });
            // Call onRequestSubmit just to close the modal.
            await onRequestSubmit([]);
          }
        },
      },
    ];
  }
  return [
    {
      text: state.isImporting
        ? 'Processing...'
        : attachAndProcessFiles
          ? state.selectedRowIndices.size === 0
            ? 'Skip'
            : 'Next'
          : 'Process',
      disabled: state.isImporting || (state.selectedRowIndices.size === 0 && !attachAndProcessFiles),
      submit: true,
      onClick: async () => {
        dispatch({
          type: ImportConnectionFilesModalActionType.IS_IMPORTING,
          isImporting: true,
        });
        try {
          const connectionFiles = state.connectionFiles;
          const selectedRowIndices = state.selectedRowIndices;
          if (attachAndProcessFiles) {
            // If attaching files, store files to process and wait for next step.
            dispatch({
              type: ImportConnectionFilesModalActionType.SET_PROCESS_CONNECTION_FILES,
              connectionFiles: [...selectedRowIndices].map((index: number) => connectionFiles[index]),
            });
          } else {
            // If not attaching files, call onRequestSubmit to process files and exit modal.
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT);
            await onRequestSubmit([...selectedRowIndices].map((index: number) => connectionFiles[index]));
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT);
          }
        } finally {
          dispatch({
            type: ImportConnectionFilesModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.ATTACH,
          });
          // The modal does not close when onRequestSubmit fails above. There's probably a better way
          // to do this, but adding this here is harmless and makes the modal close reliably.
          if (!attachAndProcessFiles) {
            await onRequestSubmit([]);
          }
        }
      },
    },
  ];
}

function getHelpText(
  currentStepKey: StepKey,
  instrumentServiceDetails: InstrumentServiceDetails | null,
  connectionId?: string
): string {
  if (currentStepKey === StepKey.PROCESS) {
    if (!instrumentServiceDetails) {
      return 'If files have loaded below, you may proceed with selecting files to upload to your run for processing.';
    } else if (connectionId && instrumentServiceDetails.id != null) {
      return `Select which files to upload to your run for processing. Connection last updated on ${DateUtil.formalTime(
        instrumentServiceDetails.status.timestamp
      )}.`;
    } else {
      return 'Select which files to upload to your run for processing.';
    }
  } else if (currentStepKey === StepKey.ATTACH) {
    return 'Select files to attach directly to your run without processing.';
  } else {
    return '';
  }
}

function getStepsList(hasAttachCallback?: boolean): Array<{key: StepKey; title: string}> {
  const stepsList: Array<{key: StepKey; title: string}> = [];
  stepsList.push({
    key: StepKey.PROCESS,
    title: 'Process files',
  });
  if (hasAttachCallback) {
    stepsList.push({
      key: StepKey.ATTACH,
      title: 'Attach files',
    });
  }
  return stepsList;
}

export default function ImportConnectionFilesModal(props: ImportConnectionFilesModalProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const defaultColumns = ['File name', 'Status', 'Modified at', 'Created at'];
  const defaultWithConnectionColumns = ['File name', 'Status', 'Connection', 'Modified at', 'Created at'];
  const defaultProcessStepColumns = ['File name', 'Status'];

  const {instrumentServiceDetails} = props;

  const buttons = getStepButtons(state, dispatch, props.onRequestSubmit, props.attachAndProcessFiles);
  const steps = useMemo(
    () => getStepsList(Boolean(props.attachAndProcessFiles)),
    [props.attachAndProcessFiles]
  );

  const setConnectionFilesCallback = useCallback(
    (connectionFiles: Array<FileListingGraphQL>): void => {
      dispatch({type: ImportConnectionFilesModalActionType.SET_CONNECTION_FILES, connectionFiles});
    },
    [dispatch]
  );

  const isConnectionAvailable = instrumentServiceDetails?.status.value === ConnectionStatus.AVAILABLE;
  const connectionIds = useMemo(() => (props.connectionId ? [props.connectionId] : []), [props.connectionId]);

  return (
    <Modal
      buttons={buttons}
      closeableOnEscapeKey={false}
      large={true}
      onRequestClose={props.onRequestClose}
      title="Retrieve data"
    >
      <StepNav<StepKey>
        steps={steps}
        statusByStep={state.statusByStep}
        currentStepKey={state.currentStepKey}
        // NOTE: we don't use step nav to move between steps, instead using buttons, so this is unused.
        onUpdateCurrentStep={undefined}
        variant={StepNavVariant.FLAT}
      />
      {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS && !isConnectionAvailable && (
        <div className={css(styles.warningBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={
              instrumentServiceDetails
                ? instrumentServiceDetails.status.value === ConnectionStatus.DISABLED
                  ? 'This connection is disabled. Please re-enable it to retrieve the latest data.'
                  : 'This connection is not currently available. Please try again later for the latest data.'
                : 'Instrument service is not available. Cannot retrieve connection status.'
            }
          />
        </div>
      )}
      <p className={css(styles.helpText)}>
        {getHelpText(state.currentStepKey, instrumentServiceDetails, props.connectionId)}
      </p>
      <ConnectionFilesTable
        connectionIds={connectionIds}
        connectionFiles={state.connectionFiles}
        setConnectionFiles={setConnectionFilesCallback}
        selectedRowIndices={state.selectedRowIndices}
        onSelectRow={(rowIndex: number): void => {
          if (
            getConnectionFileStatus(state.connectionFiles[rowIndex]) === ConnectionFileStatus.UPLOADED &&
            (props.allowMultiple ||
              state.selectedRowIndices.size === 0 ||
              state.selectedRowIndices.has(rowIndex))
          ) {
            dispatch({
              type: ImportConnectionFilesModalActionType.SELECT_ROW,
              selectedRowIndex: rowIndex,
            });
          }
        }}
        allowMultiple={props.allowMultiple}
        columns={
          state.currentStepKey === StepKey.ATTACH
            ? defaultProcessStepColumns
            : props.connectionId
              ? defaultColumns
              : defaultWithConnectionColumns
        }
      />
      {state.currentStepKey === StepKey.PROCESS && !state.isImporting && (
        <div className={css(styles.previewContainer)}>
          {[...state.selectedRowIndices].map((index) => {
            const file = state.connectionFiles[index];

            return file.scratchFile.downloadUrl &&
              endsWithAny(file.scratchFile.filename, PREVIEWABLE_FILE_TYPES) ? (
              <div key={index}>
                <p key={`${index}-title`}>{file.scratchFile.filename}</p>
                <CSVFilePreview key={`${index}-preview`} url={file.scratchFile.downloadUrl} maxRows={5} />
              </div>
            ) : (
              <p key={index} className="u-spacing-top faded text-center">
                {`Preview available only for the following file types: ${PREVIEWABLE_FILE_TYPES}`}
              </p>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

export const styles = StyleSheet.create({
  helpText: {
    'font-style': 'italic',
    padding: '10px 0 20px',
  },
  previewContainer: {
    overflowX: 'scroll',
  },
  warningBanner: {
    marginTop: '10px',
  },
});
